// Screen telemetry for process mining: which screen is open and for how long it was actually visible, and when someone
// leaves for a Business Central page. Sent in small batches; the API decides whether capture is on. Paths are reduced
// to patterns (record numbers become :id) before they leave the browser, and nothing typed on a screen is recorded.
import { config } from "./config";
import { getToken } from "./auth";

const SESSION_KEY = "pm.session";
export const session: string = (() => {
  try { let s = sessionStorage.getItem(SESSION_KEY); if (!s) { s = Math.random().toString(36).slice(2, 12); sessionStorage.setItem(SESSION_KEY, s); } return s; }
  catch { return Math.random().toString(36).slice(2, 12); }
})();

type Ev = { kind: "view" | "bc"; path: string; ms?: number; at: string };
let queue: Ev[] = [];
let current: { path: string; start: number; visible: number; since: number | null } | null = null;
let paused = false;
let started = false;

export const pattern = (path: string) => path.split("?")[0].split("/").map((s) => (/\d/.test(s) || /^[0-9a-f-]{32,36}$/i.test(s) ? ":id" : s)).join("/").replace(/\/+$/, "") || "/";

function close(now = Date.now()) {
  if (!current) return;
  const ms = current.visible + (current.since !== null ? now - current.since : 0);
  if (ms >= 1000) queue.push({ kind: "view", path: current.path, ms, at: new Date(current.start).toISOString() });
  current = null;
}

/** Call on every route change. */
export function trackView(pathname: string) {
  if (paused) return;
  start();
  const p = pattern(pathname);
  if (current?.path === p) return;
  close();
  current = { path: p, start: Date.now(), visible: 0, since: document.visibilityState === "visible" ? Date.now() : null };
  if (queue.length >= 40) void flush();
}

async function flush(keepalive = false) {
  if (!queue.length || paused) return;
  const events = queue; queue = [];
  try {
    const headers: Record<string, string> = { "content-type": "application/json", "x-session": session };
    if (config.authMode === "dev") headers["x-user"] = localStorage.getItem("devUser") ?? "Max";
    else headers.authorization = `Bearer ${await getToken()}`;
    const r = await fetch("/api/process-mining/track", { method: "POST", headers, keepalive, body: JSON.stringify({ session, events }) });
    const j = await r.json().catch(() => ({}));
    if (r.ok && j.stored === 0) paused = true; // capture is off for this company
  } catch { /* offline or signed out: drop this batch */ }
}

function start() {
  if (started) return;
  started = true;
  document.addEventListener("visibilitychange", () => {
    if (!current) return;
    if (document.visibilityState === "hidden") { if (current.since !== null) { current.visible += Date.now() - current.since; current.since = null; } close(); void flush(true); }
    else { current = { ...current, start: Date.now(), visible: 0, since: Date.now() }; }
  });
  window.addEventListener("pagehide", () => { close(); void flush(true); });
  // Leaving for Business Central: a link to a BC page, named the way the link reads.
  document.addEventListener("click", (e) => {
    const a = (e.target as HTMLElement | null)?.closest?.("a") as HTMLAnchorElement | null;
    if (!a || !a.href.includes("businesscentral.dynamics.com")) return;
    const page = a.href.match(/[?&]page=(\d+)/)?.[1] ?? "";
    const name = (a.textContent ?? "").replace(/\s+/g, " ").trim().slice(0, 60) || "Business Central page";
    queue.push({ kind: "bc", path: `bc:${name}${page ? ` · ${page}` : ""}`, at: new Date().toISOString() });
  }, true);
  setInterval(() => void flush(), 30_000);
}
